import { useState } from "react";
import { Star, Pencil, Check } from "lucide-react";
import { toast } from "sonner";
import { SPORT_META } from "@/lib/odds";
import { useDataSource, visibleSports } from "@/lib/dataSource";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

export function useFavoriteSports() {
  const { user } = useAuth();
  return user?.sports?.filter((k) => SPORT_META[k]) || [];
}

export const matchesSport = (itemSport, value, favorites) => value === "all" || (value === "mine" ? favorites.includes(itemSport) : itemSport === value);

export function SportTabs({ value, onChange, counts = {} }) {
  const { setUser } = useAuth();
  const favorites = useFavoriteSports();
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState(favorites);
  const ds = useDataSource();
  const ordered = visibleSports([...favorites, ...Object.keys(SPORT_META).filter((k) => !favorites.includes(k))], ds);
  const tabs = [{ key: "all", label: "All Sports" }, ...(favorites.length ? [{ key: "mine", label: "My Sports", star: true }] : []), ...ordered.map((key) => ({ key, label: SPORT_META[key].label, fav: favorites.includes(key) }))];
  const save = async () => {
    try {
      const { data } = await api.post("/users/me/onboard", { sports: draft, onboarded: true });
      setUser((u) => ({ ...u, sports: data.sports }));
      setEdit(false);
      toast.success(draft.length ? `${draft.length} favorite sport${draft.length > 1 ? "s" : ""} saved — they lead every slate now` : "Favorites cleared");
      if (value === "mine" && !draft.length) onChange("all");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div>
      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="sport-tabs">
        {tabs.map((t) => {
          const active = value === t.key;
          return (
            <button key={t.key} data-testid={`sport-tab-${t.key}`} onClick={() => onChange(t.key)}
              className={`flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-[background-color,border-color,color,transform] duration-200 hover:-translate-y-0.5 ${active ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-[#26334D] bg-[#121824] text-slate-400 hover:border-slate-500 hover:text-white"}`}>
              {t.star && <Star size={12} className="fill-amber-300 text-amber-300" />}
              {t.key !== "all" && !t.star && <span className="h-2 w-2 rounded-full" style={{ background: SPORT_META[t.key].color }} />}
              {t.label}
              {t.fav && <Star size={10} className="fill-amber-300/70 text-amber-300/70" data-testid="sport-tab-fav" />}
              {counts[t.key] != null && <span className="font-mono-num rounded-full bg-[#0B0E14] px-1.5 text-[11px] text-slate-400">{counts[t.key]}</span>}
            </button>
          );
        })}
        <button data-testid="sport-prefs-edit" onClick={() => { setDraft(favorites); setEdit((e) => !e); }} title="Choose favorite sports" className="flex shrink-0 items-center gap-1 rounded-full border border-dashed border-[#26334D] px-3 py-2 text-xs text-slate-500 hover:text-white"><Pencil size={12} /> {favorites.length ? "Edit favorites" : "Pick favorites"}</button>
      </div>
      {edit && (
        <div className="fade-up mt-2 flex flex-wrap items-center gap-2 rounded-xl border border-[#26334D] bg-[#121824] p-3" data-testid="sport-prefs-editor">
          {Object.entries(SPORT_META).filter(([k]) => !(ds.hidden || []).includes(k)).map(([k, m]) => { const on = draft.includes(k); return <button key={k} data-testid={`sport-pref-${k}`} onClick={() => setDraft((d) => (on ? d.filter((x) => x !== k) : [...d, k]))} className={`rounded-full border px-3 py-1 text-xs font-semibold ${on ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-[#26334D] text-slate-400 hover:text-white"}`}>{m.label}</button>; })}
          <button data-testid="sport-prefs-save" onClick={save} className="btn-primary ml-auto !px-3 !py-1.5 text-xs"><Check size={13} /> Save</button>
        </div>
      )}
    </div>
  );
}
